/**
 * Pure helpers for building the first-load Explore initiation payload from a catalog card.
 *
 * These helpers convert an `ExploreCatalogCard` + optional `ActionSelections` into
 * the `FlowMetadata` shape expected by the send flow for an EXPLORE request.
 *
 * Keeping this logic here ensures it is independently testable and reused by both
 * the card initiation path and any future entry points.
 */

import type { ExploreCatalogCard } from '../types/explore.types'
import type { ActionSelections, FlowMetadata } from '../types/chat.types'

/**
 * Build the metadata for a query-backed Explore card.
 *
 * Query cards execute immediately, so the selected menu item is sent upfront.
 * Optional parameter fields are included only when the card requires them and
 * the user has already supplied selections.
 */
export function buildExploreQueryCardInitiationMetadata(
  card: ExploreCatalogCard,
  selections?: Partial<ActionSelections>,
): FlowMetadata {
  const { initiation } = card

  const base: FlowMetadata = {
    currentMenuId: initiation.currentMenuId,
  }

  if (initiation.selectedMenuItemId) {
    base.selectedMenuItemId = initiation.selectedMenuItemId
  }

  if (!initiation.requiredCondition || !selections) {
    return base
  }

  if (initiation.requiredCondition === 'month_and_year') {
    if (selections.yearChosen != null) {
      base.yearChosen = selections.yearChosen
    }
    if (selections.monthChosen != null) {
      base.monthChosen = selections.monthChosen
    }
    return base
  }

  if (initiation.requiredCondition === 'year1_and_year2') {
    if (selections.year1 != null) {
      base.year1 = selections.year1
    }
    if (selections.year2 != null) {
      base.year2 = selections.year2
    }
    return base
  }

  return base
}

/**
 * Build the metadata for a menu-backed Explore card.
 *
 * Menu cards open the submenu first, so the first request only needs the
 * submenu menu id. The actual query selection is handled later through
 * `availableActions` in the regular Explore flow.
 */
export function buildExploreMenuCardInitiationMetadata(
  card: ExploreCatalogCard,
): FlowMetadata {
  return {
    currentMenuId: card.initiation.currentMenuId,
  }
}

/**
 * Build the initial Explore metadata for any catalog card.
 */
export function buildExploreCardInitiationMetadata(
  card: ExploreCatalogCard,
  selections?: Partial<ActionSelections>,
): FlowMetadata {
  if (card.initiation.type === 'menu') {
    return buildExploreMenuCardInitiationMetadata(card)
  }

  return buildExploreQueryCardInitiationMetadata(card, selections)
}
